import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Pagination élégante inspirée du design premium.
 * - `total` : nombre total d'éléments après filtres.
 * - `pageSize` : 12 par défaut.
 * - Affichage conditionnel : rien si <= pageSize.
 * - URL param `page` (1-indexé). Ne modifie pas les autres params (filtres préservés).
 * - Émet <link rel="prev"> / <link rel="next"> pour le SEO.
 * - `basePath` sert à générer les hrefs (utile pour SSR crawlers).
 */
export function usePaginatedSlice(total, pageSize = 12) {
  const [params] = useSearchParams();
  const raw = parseInt(params.get("page") || "1", 10);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, isNaN(raw) ? 1 : raw), totalPages);
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return { page, totalPages, start, end, pageSize };
}

function pageNumbers(current, total) {
  // Ellipse compacte : 1 ... c-1 c c+1 ... N
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const arr = Array.from(pages).filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    out.push(arr[i]);
    if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) out.push("…");
  }
  return out;
}

export default function Pagination({ page, totalPages, basePath }) {
  const [params, setParams] = useSearchParams();

  // Injection <link rel="prev">/<link rel="next"> et canonical adapté à la page courante.
  useEffect(() => {
    const BASE = "https://venteaterme.fr";
    const buildUrl = (n) => {
      const p = new URLSearchParams(params);
      if (n <= 1) p.delete("page"); else p.set("page", String(n));
      const qs = p.toString();
      return `${BASE}${basePath}${qs ? `?${qs}` : ""}`;
    };
    const setRel = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!href) { if (el) el.remove(); return; }
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };
    setRel("prev", page > 1 ? buildUrl(page - 1) : null);
    setRel("next", page < totalPages ? buildUrl(page + 1) : null);
    // Ajuste le canonical
    const canonical = buildUrl(page);
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) {
      c = document.createElement("link");
      c.setAttribute("rel", "canonical");
      document.head.appendChild(c);
    }
    c.setAttribute("href", canonical);
    return () => {
      setRel("prev", null);
      setRel("next", null);
    };
  }, [page, totalPages, params, basePath]);

  const items = useMemo(() => pageNumbers(page, totalPages), [page, totalPages]);
  if (totalPages <= 1) return null;

  const go = (n) => {
    const p = new URLSearchParams(params);
    if (n <= 1) p.delete("page"); else p.set("page", String(n));
    setParams(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const hrefFor = (n) => {
    const p = new URLSearchParams(params);
    if (n <= 1) p.delete("page"); else p.set("page", String(n));
    const qs = p.toString();
    return `${basePath}${qs ? `?${qs}` : ""}`;
  };

  const btnBase = "min-w-[42px] h-[42px] px-3 inline-flex items-center justify-center border text-sm font-bold tabular-nums transition-colors";
  return (
    <nav
      aria-label="Pagination"
      data-testid="pagination"
      className="mt-12 flex items-center justify-center flex-wrap gap-2"
    >
      {page > 1 ? (
        <Link
          to={hrefFor(page - 1)}
          onClick={(e) => { e.preventDefault(); go(page - 1); }}
          data-testid="pagination-prev"
          rel="prev"
          className={`${btnBase} border-navy/20 text-navy hover:bg-navy hover:text-cream hover:border-navy`}
          aria-label="Page précédente"
        >
          <ChevronLeft size={16} className="mr-1" /> Précédent
        </Link>
      ) : (
        <span data-testid="pagination-prev-disabled" aria-disabled="true"
          className={`${btnBase} border-navy/10 text-navy/30 cursor-not-allowed`}>
          <ChevronLeft size={16} className="mr-1" /> Précédent
        </span>
      )}

      {items.map((it, i) =>
        it === "…" ? (
          <span key={`e-${i}`} className="px-1 text-navy/40 select-none" aria-hidden="true">…</span>
        ) : (
          <Link
            key={it}
            to={hrefFor(it)}
            onClick={(e) => { e.preventDefault(); go(it); }}
            data-testid={`pagination-page-${it}`}
            aria-current={it === page ? "page" : undefined}
            className={`${btnBase} ${
              it === page
                ? "bg-navy text-cream border-navy"
                : "border-navy/20 text-navy hover:border-accent-brand hover:text-accent-brand"
            }`}
          >
            {it}
          </Link>
        )
      )}

      {page < totalPages ? (
        <Link
          to={hrefFor(page + 1)}
          onClick={(e) => { e.preventDefault(); go(page + 1); }}
          data-testid="pagination-next"
          rel="next"
          className={`${btnBase} border-navy/20 text-navy hover:bg-navy hover:text-cream hover:border-navy`}
          aria-label="Page suivante"
        >
          Suivant <ChevronRight size={16} className="ml-1" />
        </Link>
      ) : (
        <span data-testid="pagination-next-disabled" aria-disabled="true"
          className={`${btnBase} border-navy/10 text-navy/30 cursor-not-allowed`}>
          Suivant <ChevronRight size={16} className="ml-1" />
        </span>
      )}
    </nav>
  );
}
